/**
 * A panel that fails without taking the page with it.
 *
 * This exists because of a real incident: an API returning an older shape made
 * one panel throw, and because nothing caught it the entire back office went
 * blank. One card's payload changed and a manager lost the whole screen.
 *
 * **It shows the actual error, deliberately.** A friendly "Something went
 * wrong" would have been worse than the crash. A blank panel on a screen
 * somebody is working on reads as a loading state that has not finished, so
 * nobody goes looking; the crash is what said the shape had changed. Swallowing
 * it converts a loud, correct failure into a silent one — and unlike an event
 * handler, where dropping a failure protects work that already succeeded, there
 * is nobody reading a log for the back office.
 *
 * So: the panel is named, the message is printed and selectable so it can be
 * pasted to whoever can fix it, the stack goes to the console, and the rest of
 * the page carries on.
 *
 * **Wrap the panel, not the panel's insides.** This has to go *around* a
 * component, never inside one that receives `children`. `<Card>{expr}</Card>`
 * evaluates `expr` while the *parent* renders, before `Card` exists, so a throw
 * there is caught by whatever sits above the parent and never by anything Card
 * contains. Putting a boundary inside `Card` would look like blanket coverage
 * and would have missed the exact incident this was written for — the failing
 * `.map` was in the expression building a card's children. Hence
 * `<Boundary label="Plate cost"><CostPanel /></Boundary>` at the call site, and
 * one around the routed page to catch whatever is composed inline.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { reportError } from '../error-reporting';
import './boundary.css';

interface Props {
  /** What broke, in the words on screen — "Plate cost", "By tender". */
  label?: string | undefined;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class Boundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The stack is the useful half and does not belong on screen. Console is
    // where a developer already is when a panel has broken in front of them.
    console.error(`[${this.props.label ?? 'panel'}] render failed`, error, info.componentStack);
    // And to error reporting, where one is configured — the reader this
    // comment says the back office never had.
    reportError(error, { panel: this.props.label ?? 'panel' });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="ui-broke" role="alert">
        <p className="ui-broke__title">
          {this.props.label
            ? `${this.props.label} could not be shown`
            : 'This could not be shown'}
        </p>
        {/* Verbatim and selectable. The exact words are what identifies the
            fault to whoever can fix it, and paraphrasing loses them. */}
        <p className="ui-broke__message">{error.message}</p>
        <p className="ui-broke__hint">
          The rest of this page still works. If this followed an update, the screen and the
          server may disagree about a shape — reloading is worth trying once.
        </p>
        <button
          type="button"
          className="ui-broke__retry"
          onClick={() => this.setState({ error: null })}
        >
          Try again
        </button>
      </div>
    );
  }
}
